import type { ComponentType, ReactNode } from 'react'
import { visit } from 'unist-util-visit'

import type { DirectiveNode } from './admonitions.ts'
import { PayloadWebhookEventsExplorer } from './components/PayloadWebhookEventsExplorer.tsx'
import { WebhookEndpointSetup } from './components/WebhookEndpointSetup.tsx'
import { WebhookLifecycleExplorer } from './components/WebhookLifecycleExplorer.tsx'
import { WebhookPayloadExample } from './components/WebhookPayloadExample.tsx'

// Interactive components mounted from plain markdown.
//
// The docs are deliberately plain markdown, not MDX (see content/README.md and
// the guard in tests/docs-manifest.unit.test.ts) — but some pages need a live
// React component in the middle of the prose. `remark-directive` is already in
// the pipeline for admonitions, so a directive is the mounting point: a
// `::name` line in a content file renders the component registered under that
// name here. Adding a component is one entry in `DOC_DIRECTIVES` plus the
// directive line in the page — no renderer changes.
//
// Directive components render inside the shell's providers, so one that needs
// the session (a future "send a test webhook" button, say) can use
// `useApiFetch()` and follow the mutation pattern in
// routes/webhooks/AttemptsCard.tsx.

export interface DirectiveProps {
  /** `::name{key="value"}` attributes from the directive, verbatim. */
  attrs: Record<string, string>
  /** The docs' SPA navigation, for components that render internal links. */
  onNavigate?: (path: string) => void
  children?: ReactNode
}

export const DOC_DIRECTIVES: Record<string, ComponentType<DirectiveProps>> = {
  'payload-webhook-events': PayloadWebhookEventsExplorer,
  'webhook-endpoint': WebhookEndpointSetup,
  'webhook-lifecycle': WebhookLifecycleExplorer,
  'webhook-payload': WebhookPayloadExample,
}

const isRegistered = (name: string): boolean =>
  Object.prototype.hasOwnProperty.call(DOC_DIRECTIVES, name)

// The same trick remarkAdmonitions uses: `data.hName`/`hProperties` survive
// rehype-raw and reach react-markdown's component map, where the `section`
// override dispatches on the marker back into `DOC_DIRECTIVES`. Attributes ride
// along JSON-encoded because hProperties values must be serializable.
export const remarkDocDirectives = () => (tree: DirectiveNode) => {
  visit(tree, ['leafDirective', 'containerDirective'], (node: DirectiveNode) => {
    const name = node.name ?? ''
    // An unregistered `::something` is left as-is, same as an unrecognised
    // admonition — silently swallowing it is how a typo becomes invisible.
    if (!isRegistered(name)) return

    const attributes = Object.fromEntries(
      Object.entries(node.attributes ?? {}).filter(
        (entry): entry is [string, string] => typeof entry[1] === 'string'
      )
    )

    node.data = {
      ...node.data,
      hName: 'section',
      hProperties: {
        'data-directive': name,
        'data-directive-attrs': JSON.stringify(attributes),
      },
    }
  })
}
